import { useState, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { supabase } from '@/lib/supabase';
import { getInvalidationKeys } from '@/lib/query-keys';
import { captureError } from '@/lib/sentry';

const editGroupSchema = z.object({
  name: z.string().min(1, 'Group name is required').max(100, 'Group name is too long'),
});

interface EditGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string;
  currentName: string;
  onSuccess?: () => void;
}

export function EditGroupDialog({
  open,
  onOpenChange,
  groupId,
  currentName,
  onSuccess,
}: EditGroupDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { currentWorkspace } = useWorkspaces();
  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof editGroupSchema>>({
    resolver: zodResolver(editGroupSchema),
    defaultValues: {
      name: currentName,
    },
  });

  // Reset form when dialog opens with current name
  useEffect(() => {
    if (open) {
      form.reset({ name: currentName });
    }
  }, [open, currentName, form]);

  const handleSubmit = async (values: z.infer<typeof editGroupSchema>) => {
    if (!currentWorkspace) return;

    const newName = values.name.trim().toLowerCase();

    // Skip if name hasn't changed
    if (newName === currentName.toLowerCase()) {
      onOpenChange(false);
      return;
    }

    setIsLoading(true);
    try {
      // Check for duplicate name in workspace
      const { data: existing, error: checkError } = await supabase
        .from('groups')
        .select('id')
        .eq('workspace_id', currentWorkspace.id)
        .eq('group_name', newName)
        .neq('id', groupId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existing) {
        form.setError('name', {
          type: 'manual',
          message: 'A group with this name already exists',
        });
        setIsLoading(false);
        return;
      }

      // Update the group name
      const { error: updateError } = await supabase
        .from('groups')
        .update({ group_name: newName })
        .eq('id', groupId);

      if (updateError) throw updateError;

      toast({
        description: `Group renamed to "${values.name}"`,
      });

      // Invalidate groups queries to refresh nav and dropdowns
      const invalidationKeys = getInvalidationKeys.afterGroupMutation(currentWorkspace.id);
      await Promise.all(
        invalidationKeys.map(key => queryClient.invalidateQueries({ queryKey: key }))
      );

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error updating group:', error);
      captureError(error as Error, { feature: 'groups-edit-group-save' });
      toast({
        title: 'Error',
        description: 'Failed to update group name. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Group</DialogTitle>
          <DialogDescription>
            Change the group name. This will update it everywhere in the system.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Group Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter group name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
